import React from 'react';

import {
  SuccessIcon,
  OverpaymentDivider,
  OverpaymentMessage,
  OverpaymentRow,
  OverpaymentLabel,
  OverpaymentValue,
  OverpaymentAmountLabel,
  OverpaymentAmountValue,
  OverpaymentHint,
  DetectedContinueWrapper,
  DetectedContinueButton,
} from 'screens/Authenticated/PaymentQR/styled';

import {
  Backdrop,
  Card,
  CloseButton,
  Title,
  Subtitle,
} from './styled';

/**
 * Inline check svg — same glyph as PaymentQR/SuccessIcon. Duplicated
 * here rather than imported across screen boundaries because this
 * modal lives outside the payment-flow tree (rendered by the global
 * TournamentWrapper after a confirmed overpayment).
 */
const CheckGlyph: React.FC<{ size?: number }> = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12.5l4 4 10-10"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface OverpaymentData {
  expectedAmount: number;
  paidAmount: number;
  currency: string;
}

interface OverpaymentModalProps {
  data: OverpaymentData;
  onClose: () => void;
}

const OverpaymentModal: React.FC<OverpaymentModalProps> = ({ data, onClose }) => {
  const { expectedAmount, paidAmount, currency } = data;
  const overpaymentAmount = Math.max(0, paidAmount - expectedAmount);

  return (
    <Backdrop onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          <svg width={14} height={14} viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M2 2l10 10M12 2L2 12"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </CloseButton>

        <SuccessIcon>
          <CheckGlyph />
        </SuccessIcon>

        <Title>
          You have <span>joined</span> the tournament!
        </Title>

        <Subtitle>
          The tournament link has been also sent to your email (if you provided one)
        </Subtitle>

        <OverpaymentDivider />

        <OverpaymentMessage>
          Your transaction was successful, but you sent <b>more than the required stake</b>
        </OverpaymentMessage>

        <OverpaymentRow>
          <OverpaymentLabel>Expected:</OverpaymentLabel>
          <OverpaymentValue>
            {expectedAmount.toFixed(2)} {currency}
          </OverpaymentValue>
        </OverpaymentRow>
        <OverpaymentRow>
          <OverpaymentLabel>Received:</OverpaymentLabel>
          <OverpaymentValue>
            {paidAmount.toFixed(2)} {currency}
          </OverpaymentValue>
        </OverpaymentRow>

        <OverpaymentAmountLabel>Overpayment:</OverpaymentAmountLabel>
        <OverpaymentAmountValue>
          {overpaymentAmount.toFixed(2)} {currency}
        </OverpaymentAmountValue>

        <OverpaymentHint>
          Excess funds will be refunded after the tournament if they cover the transaction fee
        </OverpaymentHint>

        <DetectedContinueWrapper>
          <DetectedContinueButton type="button" onClick={onClose}>
            Continue
          </DetectedContinueButton>
        </DetectedContinueWrapper>
      </Card>
    </Backdrop>
  );
};

export default OverpaymentModal;
